import {
  FillStructuredTablePartialResponse,
  FillStructuredTableUsingAIMatchResultResponse,
  FillStructuredTableUsingAIPairwiseComparisonResponse,
  FillStructuredTableUsingAIResponse,
  FillStructuredTableUsingAIRunResultResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import sortedBy from 'util/sortedBy';

export enum MismatchErrorType {
  CELL_MISMATCH = 'CELL_MISMATCH',
  ROW_MISMATCH = 'ROW_MISMATCH',
  USER_EDIT = 'USER_EDIT',
}

/**
 * Error type specific to cross-model validation for displaying mismatches.
 */
export interface SimpleCellError {
  /**
   * Type of mismatch error (cell-level or row-level).
   */
  type: MismatchErrorType;
  /**
   * Message describing the mismatch.
   */
  message: string;
}

/**
 * A simplified representation of a cell for cross-model validation.
 */
export interface SimpleCellData {
  /**
   * The original row index of the cell (for filtering).
   */
  originalRowIndex: number;
  /**
   * The primary text content of the cell.
   */
  primaryText: string;
  /**
   * The secondary text content of the cell (for mismatch cases).
   */
  secondaryText: string | null;
  /**
   * The manually entered text content of the cell.
   */
  userText: string | null;
  /**
   * Optional error information if the cell has validation issues.
   */
  error: SimpleCellError | null;
}

/**
 * Diff-like information to show to the user in cross-model validation.
 */
export interface CrossModelComparisonTables {
  /**
   * Primary model results that will be shown to the user as the rows that will be inserted.
   */
  primaryTable: Array<Array<SimpleCellData>>;
  /**
   * Secondary tables showing extra rows that *won't* be inserted but were produced by the non-primary
   * models.
   */
  secondaryExtraTables: Array<{
    displayName: string;
    table: Array<Array<SimpleCellData>>;
  }>;
}

export function getComparisonCellDisplayText(cell: SimpleCellData): string {
  if (cell.userText != null) {
    return cell.userText;
  }
  if (cell.secondaryText != null) {
    return `${cell.primaryText} | ${cell.secondaryText}`;
  }
  return cell.primaryText;
}

function mismatchError(message: string, type: MismatchErrorType): SimpleCellError {
  return {type, message};
}

/**
 * We consider a result to be verified if any two runs from different model families agree.
 */
export function isCrossModelValidationVerified(fillResult: FillStructuredTableUsingAIResponse): boolean {
  return getFirstVerifiedPairwiseComparison(fillResult.pairwiseComparisons, fillResult.runResults) != null;
}

/**
 * Render the table cells of a diff view allowing the possibility of a mismatch.
 *
 * In addition to returning the table to show to the user, we include any extra rows from other
 * runs that didn't cleanly match the table.
 */
export function generateCrossModelComparisonTables(
  fillResult: FillStructuredTableUsingAIResponse,
  primaryIndex: number = 0,
  tableName: string
): CrossModelComparisonTables {
  const primaryDisplayName = fillResult.runResults[primaryIndex].displayName;
  const secondaryDisplayName = fillResult.runResults[primaryIndex === 0 ? 1 : 0].displayName;
  const pairwiseComparison = fillResult.pairwiseComparisons[0];

  const primaryMatchResults = sortedBy(
    (pairwiseComparison?.matchResults ?? []).filter(
      (matchResult) =>
        (primaryIndex === 0 ? matchResult.row1 != null : matchResult.row2 != null) &&
        matchResult.tableName === tableName
    ),
    [{accessor: (matchResult) => (primaryIndex === 0 ? matchResult.row1!.index : matchResult.row2!.index)}]
  );

  if (primaryMatchResults.length === 0) {
    return {
      primaryTable: (fillResult.runResults[0]?.newRows[tableName] ?? []).map((row, rowNumber) =>
        row.map(
          (cell): SimpleCellData => ({
            originalRowIndex: rowNumber,
            primaryText: cell.text,
            secondaryText: null,
            userText: null,
            error: mismatchError(
              `Model ${fillResult.runResults[1].displayName} failed and returned no response.`,
              MismatchErrorType.ROW_MISMATCH
            ),
          })
        )
      ),
      secondaryExtraTables: [],
    };
  }

  const primaryTable = primaryMatchResults.map((matchResult, rowNumber) => {
    const primaryRow = primaryIndex === 0 ? matchResult.row1! : matchResult.row2!;
    const secondaryRow = primaryIndex === 0 ? matchResult.row2 : matchResult.row1;
    return generateComparisonRow(
      {
        ...matchResult,
        row1: primaryRow,
        row2: secondaryRow,
      },
      primaryDisplayName,
      secondaryDisplayName,
      rowNumber
    );
  });

  const leftoverMatchResults = sortedBy(
    (pairwiseComparison?.matchResults ?? []).filter(
      (matchResult) =>
        (primaryIndex === 0 ? matchResult.row1 == null : matchResult.row2 == null) &&
        matchResult.tableName === tableName
    ),
    [{accessor: (matchResult) => (primaryIndex === 0 ? matchResult.row2!.index : matchResult.row1!.index)}]
  );

  if (leftoverMatchResults.length === 0) {
    return {primaryTable, secondaryExtraTables: []};
  } else {
    return {
      primaryTable,
      secondaryExtraTables: [
        {
          displayName: secondaryDisplayName,
          table: leftoverMatchResults.map((matchResult, rowNumber) => {
            const row = primaryIndex === 0 ? matchResult.row2! : matchResult.row1!;
            return row.row.map((cell) => ({
              originalRowIndex: rowNumber,
              primaryText: cell.text,
              secondaryText: null,
              userText: null,
              error: mismatchError(
                `Model ${primaryDisplayName} did not produce a corresponding row.`,
                MismatchErrorType.ROW_MISMATCH
              ),
            }));
          }),
        },
      ],
    };
  }
}

function generateComparisonRow(
  matchResult: FillStructuredTableUsingAIMatchResultResponse,
  primaryDisplayName: string,
  secondaryDisplayName: string,
  rowNumber: number
): Array<SimpleCellData> {
  const row1 = matchResult.row1!;
  const row2 = matchResult.row2;
  if (row2) {
    return row1.row.map((cell1, i): SimpleCellData => {
      const cell2 = row2.row[i];
      if (cell1.text === cell2.text) {
        return {
          primaryText: cell1.text,
          originalRowIndex: rowNumber,
          secondaryText: null,
          userText: null,
          error: null,
        };
      } else {
        return {
          primaryText: cell1.text,
          secondaryText: cell2.text,
          originalRowIndex: rowNumber,
          userText: null,
          error: mismatchError(
            `${primaryDisplayName}: ${cell1.text} | ${secondaryDisplayName}: ${cell2.text}`,
            MismatchErrorType.CELL_MISMATCH
          ),
        };
      }
    });
  } else {
    return row1.row.map(
      (cell): SimpleCellData => ({
        primaryText: cell.text,
        originalRowIndex: rowNumber,
        secondaryText: null,
        userText: null,
        error: mismatchError(
          `Model ${secondaryDisplayName} did not produce a corresponding row.`,
          MismatchErrorType.ROW_MISMATCH
        ),
      })
    );
  }
}

/**
 * Based on a partial response, determine if we can come to a conclusion early, and return such
 * a response if so.
 *
 * If we see two runs from different model families that verify each other, we consider that
 * sufficient to be considered validated.
 */
export function getEarlyDataEntryResponse(
  partialResponse: FillStructuredTablePartialResponse
): FillStructuredTableUsingAIResponse | null {
  return findResponseForVerifiedRuns(
    partialResponse.pairwiseComparisons,
    partialResponse.runs.map((run) => run.results)
  );
}

/**
 * For now, when handling a data entry response with many runs, we simplify it to two representative runs.
 *
 * If any pairwise comparison from different families matches, we use that match. In some cases, we will
 * have early-returned with a match before this function is called, but it's also possible for the last
 * run result to be the one that matches.
 *
 * If there is no verified match, we just use the first two runs, which are assumed to be the most accurate.
 */
export function getSimplifiedDataEntryResponse(
  response: FillStructuredTableUsingAIResponse
): FillStructuredTableUsingAIResponse {
  const verifiedRunsResponse = findResponseForVerifiedRuns(response.pairwiseComparisons, response.runResults);
  if (verifiedRunsResponse != null) {
    return verifiedRunsResponse;
  } else {
    return {
      runResults: response.runResults.slice(0, 2),
      pairwiseComparisons: response.pairwiseComparisons.filter(
        (comparison) => comparison.runIndex1 === 0 && comparison.runIndex2 === 1
      ),
    };
  }
}

/**
 * Detect if there is a valid verified run pair, verifying across different model families. If so,
 * return a synthetic response with just those two runs.
 */
function findResponseForVerifiedRuns(
  pairwiseComparisons: Array<FillStructuredTableUsingAIPairwiseComparisonResponse>,
  runResults: Array<FillStructuredTableUsingAIRunResultResponse | null>
): FillStructuredTableUsingAIResponse | null {
  const pairwiseComparison = getFirstVerifiedPairwiseComparison(pairwiseComparisons, runResults);
  if (pairwiseComparison != null) {
    return {
      runResults: [runResults[pairwiseComparison.runIndex1]!, runResults[pairwiseComparison.runIndex2]!],
      pairwiseComparisons: [{...pairwiseComparison, runIndex1: 0, runIndex2: 1}],
    };
  }
  return null;
}

/**
 * Return the first pairwise comparison that we find that is independently verified by multiple
 * model families.
 */
function getFirstVerifiedPairwiseComparison(
  pairwiseComparisons: Array<FillStructuredTableUsingAIPairwiseComparisonResponse>,
  runResults: Array<FillStructuredTableUsingAIRunResultResponse | null>
): FillStructuredTableUsingAIPairwiseComparisonResponse | null {
  for (const pairwiseComparison of pairwiseComparisons) {
    if (pairwiseComparison.isExactMatch) {
      const runResults1 = runResults[pairwiseComparison.runIndex1]!;
      const runResults2 = runResults[pairwiseComparison.runIndex2]!;
      if (runResults1.modelFamily !== runResults2.modelFamily) {
        return pairwiseComparison;
      }
    }
  }
  return null;
}
